import { ElementType, forwardRef } from 'react';
import { PolymorphicComponentPropWithRef, PolymorphicRef } from '../../type-utils';
import { Chip, ChipProps } from '../chip';
import { useSwitchChipGroupContext } from './switch-chip-group-context';
import { SwitchChipGroupStyles } from './switch-chip.styles';

export type SwitchChipProps = Omit<ChipProps, 'onClick'> & {
  value: string;
  disabled?: boolean;
  isNew?: boolean;
};

type PolymorphicSwitchChipProps<T extends ElementType> = PolymorphicComponentPropWithRef<T, SwitchChipProps>;

type PolymorphicSwitchChipType = (<C extends ElementType = 'div'>(
  props: PolymorphicSwitchChipProps<C>
) => React.ReactNode) & {
  displayName?: string;
};

export const SwitchChip: PolymorphicSwitchChipType = forwardRef(
  <T extends ElementType = 'div'>(
    { as, value, disabled, isNew, ...props }: PolymorphicSwitchChipProps<T>,
    ref: PolymorphicRef<T>
  ) => {
    const { value: contextValue, toggleValue, disabled: contextDisabled } = useSwitchChipGroupContext();
    const resolvedDisabled = contextDisabled || disabled;
    const selected = contextValue.includes(value);
    const styles = SwitchChipGroupStyles.switchChip({ selected, disabled: resolvedDisabled, isNew });
    const Component = as || 'div';

    return (
      <Chip
        as={Component}
        css={styles}
        tabIndex={resolvedDisabled ? -1 : 0}
        {...(props as any)}
        onClick={
          resolvedDisabled
            ? undefined
            : (e) => {
                e.stopPropagation();
                toggleValue(value);
              }
        }
        onKeyDown={
          resolvedDisabled
            ? undefined
            : (e) => {
                if (e.key === 'Enter' || e.key === 'Space') {
                  e.stopPropagation();
                  toggleValue(value);
                }
              }
        }
        size='large'
        variant='outline'
        ref={ref}
      />
    );
  }
);

SwitchChip.displayName = 'SwitchChip';
